import type { OnboardingStepId } from './onboarding-checklist-storage';

/** תוכן תצוגה בלבד לדיאלוג אשף ההתקנה — ללא שינוי לוגיקת מערכת */
export type OnboardingStepPopupContent = {
  /** כותרת בדיאלוג (יכולה להתאים ל־label או להרחיב) */
  title: string;
  /** פסקת פתיחה קצרה */
  summary: string;
  /** שלבים ממוספרים — מה לעשות ביעד */
  steps: string[];
  /** טיפ אופציונלי */
  tip?: string;
  /** שורה קצרה לתפריט עזרה (secondary) */
  menuHint: string;
};

const POPUP_BY_ID: Record<OnboardingStepId, OnboardingStepPopupContent> = {
  accountSetup: {
    title: 'פתיחת חשבון ב Funner',
    summary:
      'יצרתם חשבון והגדרתם משתמש מנהל — נקודת הפתיחה של העבודה ב-FUNNER. השלב הזה כבר מאחוריכם.',
    steps: [
      'נרשמתם למערכת והשלמתם את פתיחת החשבון.',
      'הוגדר משתמש מנהל מערכת עם הרשאות מתאימות.',
      'מכאן ממשיכים להגדרות העסק, חיבורי ערוצים וצוות.',
    ],
    menuHint: 'חשבון ומנהל מערכת — הושלם',
  },
  whatsapp: {
    title: 'חשבון ומספר וואטסאפ',
    summary:
      'רישום המספר מתבצע ממסך הגדרות המערכת (התראות פייסבוק / וואטסאפ). לפני שמתחילים — ודאו שיש לכם את מה שנדרש ברשימה בחלון הקופץ.',
    steps: [
      'נכנסים להגדרות מערכת › התראות פייסבוק (או דרך «רישום מספר» באשף).',
      'מבצעים התחברות לפייסבוק ו-Embedded Signup לפי ההנחיות.',
      'משלימים אימות מספר (SMS או שיחה) ומוודאים שהמספר פעיל.',
    ],
    tip: 'אחרי רישום מוצלח — עוברים מיד לשלב «התכתבות ראשונה» לבדיקה מהירה.',
    menuHint: 'רישום מספר — הגדרות פייסבוק / וואטסאפ',
  },
  firstConversation: {
    title: 'התכתבות ראשונה',
    summary:
      'בדיקה מהירה שהחיבור עובד: שולחים הודעה מהוואטסאפ האישי, רואים אותה נכנסת לפאנר, ומשיבים מהמערכת.',
    steps: [
      'שולחים הודעת בדיקה מהטלפון האישי שלכם למספר העסק שחיברתם.',
      'נכנסים למסך הצ׳אטים — השיחה תופיע ברשימה (ייתכן עיכוב קצר).',
      'פותחים את השיחה, כותבים תשובה בתיבת ההודעה ושולחים — כך מוודאים קבלה ושליחה.',
    ],
    tip: 'אפשר לשלוח משהו פשוט כמו «שלום, בדיקה» — רק כדי לראות שהכל זורם.',
    menuHint: 'בדיקת שיחה — שליחה מהטלפון ותשובה מהפאנר',
  },
  company: {
    title: 'בניית זהות העסק',
    summary: 'מגדירים פרטי חברה, אזור זמן ושעות פעילות — כדי שהמערכת תדבר בשפה של העסק.',
    steps: [
      'נכנסים להגדרות אודות החברה.',
      'ממלאים שם, פרטים ושעות פעילות.',
      'שומרים ומוודאים שהכל נשמר.',
    ],
    menuHint: 'פרטי חברה ושעות פעילות',
  },
  geminiAi: {
    title: 'הגדרת טוקן AI (Gemini)',
    summary:
      'מחברים מפתח Google AI (Gemini) כדי להפעיל עוזר AI, תמלול, תבניות חכמות ועוד. ההגדרה מתבצעת במסך חיבורי המערכת.',
    steps: [
      'נכנסים להגדרות מערכת › חיבורים (Gemini).',
      'לוחצים «חיבור» ומדביקים מפתח API מ-Google AI Studio.',
      'בוחרים מודל ברירת מחדל, שומרים ומוודאים שהסטטוס «מחובר».',
    ],
    tip: 'אפשר לבדוק את החיבור עם «בדיקת LLM» אחרי השמירה.',
    menuHint: 'מפתח Gemini — הגדרות חיבורים',
  },
  users: {
    title: 'מזמינים את הצוות למערכת',
    summary: 'מוסיפים משתמשים ומגדירים הרשאות — כדי שכל אחד יקבל בדיוק את מה שצריך.',
    steps: [
      'פותחים את ניהול המשתמשים.',
      'מזמינים או עורכים משתמשים.',
      'מקצים הרשאות ושומרים.',
    ],
    menuHint: 'משתמשים והרשאות',
  },
  contacts: {
    title: 'מייבאים את הלקוחות הראשונים',
    summary: 'יוצרים או מייבאים אנשי קשר — הבסיס לשיחות ולשליחת הודעות.',
    steps: [
      'נכנסים לאזור אנשי הקשר (מסלול צ׳אט).',
      'מוסיפים ידנית או מייבאים רשימה.',
      'בודקים שהרשומות נשמרות.',
    ],
    menuHint: 'אנשי קשר לצ׳אט',
  },
  template: {
    title: 'מגישים את התבנית הראשונה',
    summary: 'בונים תבנית הודעה ומגישים לאישור — כדי לאפשר שליחה תקנית ומסודרת.',
    steps: [
      'פותחים יצירת תבנית חדשה.',
      'ממלאים תוכן ושדות נדרשים.',
      'שולחים להגשה וממתינים לאישור.',
    ],
    tip: 'הנוסח תלוי במדיניות הספק — עקבו אחרי ההודעות במסך התבנית.',
    menuHint: 'יצירה והגשת תבנית',
  },
};

export function getOnboardingStepPopupContent(id: OnboardingStepId): OnboardingStepPopupContent {
  return POPUP_BY_ID[id];
}

/** שורה קצרה לתפריט עזרה */
export function getOnboardingStepMenuHint(id: OnboardingStepId): string {
  return POPUP_BY_ID[id].menuHint;
}
